
import { useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertTriangle, Check, Copy, House, MessageSquare, RotateCcw, Send } from 'lucide-react';
import { errorReportsApi } from '@/shared/api';
import { telemetryUrl } from '@/shared/api/report-url';
import { Button, Card, Field, Input, Scroller, Textarea } from '@/shared/ui';
import styles from './ErrorScreen.module.css';

function describeError(error: unknown): { message: string; stack?: string } {
  if (isRouteErrorResponse(error)) {
    return { message: `${error.status} ${error.statusText}` };
  }
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }
  return { message: String(error ?? 'Неизвестная ошибка') };
}

const CopyButton: React.FC<{ text: string; label: string; className?: string }> = ({
  text,
  label,
  className,
}) => {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      },
      () => setCopied(false),
    );
  };

  return (
    <Button variant="ghost" size="sm" onClick={copy} className={className}>
      {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
      {copied ? 'Скопировано' : label}
    </Button>
  );
};

export function ErrorScreen({ error }: { error: unknown }) {
  const { message, stack } = describeError(error);
  const [comment, setComment] = useState('');
  const [contact, setContact] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [reportId, setReportId] = useState<string | null>(null);

  const handleSend = async () => {
    setSendState('sending');
    try {
      const res = await errorReportsApi.send({
        message,
        stack,
        url: telemetryUrl(window.location.href),
        userAgent: navigator.userAgent,
        comment: comment.trim() || undefined,
        contact: contact.trim() || undefined,
      });
      setReportId(res.data?.id ?? null);
      setSendState('sent');
    } catch {
      setSendState('failed');
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.glow} aria-hidden />
      <Card className={styles.card} padded={false}>
        <header className={styles.head}>
          <span className={styles.icon}>
            <AlertTriangle size={22} aria-hidden />
          </span>
          <h1 className={styles.title}>Что-то пошло не так</h1>
          <p className={styles.subtitle}>
            Страница не смогла отрисоваться. Данные при этом не потерялись — обычно помогает
            перезагрузка. Если ошибка повторяется, отправьте отчёт: он попадёт прямо в журнал
            поддержки вместе с адресом страницы.
          </p>
        </header>

        <div className={styles.actions}>
          <Button variant="primary" onClick={() => window.location.reload()}>
            <RotateCcw size={15} aria-hidden />
            Перезагрузить
          </Button>
          <Button variant="secondary" onClick={() => window.location.assign('/')}>
            <House size={15} aria-hidden />
            На главную
          </Button>
          {sendState !== 'sent' && !reportOpen && (
            <Button variant="ghost" className={styles.reportOpen} onClick={() => setReportOpen(true)}>
              <MessageSquare size={15} aria-hidden />
              Сообщить о проблеме
            </Button>
          )}
        </div>

        {sendState === 'sent' && (
          <section className={styles.sentBlock}>
            <span className={styles.sentTitle}>
              <Check size={15} aria-hidden />
              Отчёт отправлен
            </span>
            <p className={styles.sentNote}>
              Мы разберёмся и свяжемся с вами, если понадобятся детали.
            </p>
            {reportId && (
              <div className={styles.reportId}>
                <span className={styles.reportIdLabel}>Номер отчёта</span>
                <code className={styles.reportIdValue}>{reportId}</code>
                <CopyButton text={reportId} label="Скопировать" className={styles.quiet} />
              </div>
            )}
          </section>
        )}

        {sendState !== 'sent' && reportOpen && (
          <section className={styles.reportForm}>
            <div className={styles.reportHead}>
              <span className={styles.reportTitle}>Отчёт в поддержку</span>
              <p className={styles.reportHint}>
                Адрес страницы, браузер и техническая часть ошибки прикладываются автоматически.
                Оба поля ниже — по желанию.
              </p>
            </div>
            <Field label="Что вы делали, когда возникла ошибка">
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
            </Field>
            <Field label="Как с вами связаться" hint="Email или телефон">
              <Input value={contact} onChange={(e) => setContact(e.target.value)} />
            </Field>
            {sendState === 'failed' && (
              <p className={styles.failedNote}>
                Не удалось отправить — похоже, нет связи с сервером. Попробуйте ещё раз позже.
              </p>
            )}
            <div className={styles.reportActions}>
              <Button onClick={handleSend} disabled={sendState === 'sending'}>
                <Send size={14} aria-hidden />
                {sendState === 'sending' ? 'Отправка…' : 'Отправить отчёт'}
              </Button>
              <Button variant="ghost" onClick={() => setReportOpen(false)}>
                Отмена
              </Button>
            </div>
          </section>
        )}

        {import.meta.env.DEV && (
          <section className={styles.devBlock}>
            <div className={styles.devHead}>
              <span className={styles.devLabel}>Стек · виден только в разработке</span>
              <CopyButton text={`${message}\n${stack ?? ''}`} label="Скопировать" className={styles.quiet} />
            </div>
            <Scroller element="pre" axis="both" className={styles.devStack}>
              <strong>{message}</strong>
              {'\n'}
              {stack}
            </Scroller>
          </section>
        )}
      </Card>
    </div>
  );
}

export function RouteErrorScreen() {
  return <ErrorScreen error={useRouteError()} />;
}
